import React, { useEffect, useState } from 'react';
import { View, TouchableOpacity, Clipboard, ScrollView, ActivityIndicator } from 'react-native';

import Modal from './shared/Modal';
import Text from './shared/Text';
import Button from './shared/Button';
import Image from './shared/Image';
import { InfoIcon } from './common/Info';
import WarriorLoginForm from './common/WarriorLoginForm';

import heroStore from '../stores/hero';
import appStore from '../stores/app';
import warriorInfoModalStore from './common/WarriorInfoModal/store';

import { onFriendsChanged, getWarriorByLogin } from '../lib/dbCalls';
import { addFriend as addFriendApi } from '../lib/apiCalls';
import { INVITE_LINK_BASE } from '../lib/config';
import { t } from '../utils/appUtils';
import { BASE_COLOR, GOLDEN100, MARGIN_5, MARGIN_10, pOrT, MARGIN_15 } from './styleConstants';
import { FriendsType } from '../lib/types';
import { IS_WEB } from '../lib/appConstants';

type Props = { onHide: () => void };

export default function FriendsModal({ onHide }: Props) {
  const [friends, setFriends] = useState<FriendsType>([]);
  const [friendWarriors, setFriendWarriors] = useState<Record<string, any>>({});
  const [friendsFetching, setFriendsFetching] = useState(false);
  const [bonusesExpanded, setBonusesExpanded] = useState(false);
  const [fields, setFields] = useState<{ login: string }>({ login: '' });
  const [selectedWarrior, setSelectedWarrior] = useState<any>(null);

  const mentorBonuses = appStore.initData.mentorBonuses || [];

  useEffect(() => {
    const ref = onFriendsChanged(heroStore.hero.id, (snap: any) => {
      const val = snap.val();
      setFriends(!val ? [] : Array.isArray(val) ? val : Object.values(val));
    });
    return () => {
      (ref as any).off();
    };
  }, []);

  useEffect(() => {
    if (!friends.length) {
      setFriendWarriors({});
      setFriendsFetching(false);
      return undefined;
    }
    let cancelled = false;
    setFriendsFetching(true);
    Promise.all(friends.map((f) => getWarriorByLogin(f.warrior))).then((warriors) => {
      if (cancelled) return;
      const map: Record<string, any> = {};
      warriors.forEach((w, i) => {
        if (w) map[friends[i].warrior] = w;
      });
      setFriendWarriors(map);
      setFriendsFetching(false);
    });
    return () => {
      cancelled = true;
    };
  }, [friends]);

  const inviteLink = INVITE_LINK_BASE + heroStore.hero.login;

  const onShare = () => {
    Clipboard.setString(inviteLink);
    appStore.showMessage({ type: 'success', text: t('copy') + ' ✓' });
  };

  const validate = (): string | undefined => {
    if (!fields.login) return t('errors.allFieldsRequired');
    if (!selectedWarrior) return t('warriorNotFound');
    if (selectedWarrior.login === heroStore.hero.login) return t('errors.cantSendToSelf');
    if (friends.some((f) => f.warrior === selectedWarrior.login)) return t('errors.alreadyFriend');
    return undefined;
  };

  const onAdd = async () => {
    const msg = validate();
    if (msg) {
      appStore.showMessage({ type: 'error', text: msg });
      return;
    }
    await addFriendApi(selectedWarrior.id);
    appStore.showMessage({ type: 'success', text: t('friends.friendAdded') });
    setFields({ login: '' });
    setSelectedWarrior(null);
  };

  const showMentorHint = heroStore.hero.level <= 2 && friends.length === 0;

  return (
    <Modal
      title={t('friends.title')}
      onHide={onHide}
      size={{ width: pOrT(500, 600), height: pOrT(350, 600) }}
      warriorListener
    >
      <View style={{ marginLeft: 30, marginTop: pOrT(30, 50), marginBottom: pOrT(25, 25), marginRight: 30, flex: 1 }}>
        <ScrollView>
          {/* Invite link */}
          <Text bold>{t('friends.inviteText')}</Text>
          <View
            style={{
              flexDirection: 'row',
              marginTop: MARGIN_10,
              alignItems: 'center',
            }}
          >
            <Text bold style={{ fontSize: pOrT(10, 12), marginRight: MARGIN_15 }}>{inviteLink}</Text>
            <Button onPress={onShare}>{t('copy')}</Button>
          </View>

          {/* Add friend row */}
          <View style={{ marginTop: MARGIN_10 * 2 }}>
            <Text bold style={{ fontSize: pOrT(10, 12), marginBottom: MARGIN_10 }}>
              {t('friends.addFriendHeading')}
              {showMentorHint ? ` (${t('friends.mentorHint')})` : ''}
            </Text>
            <View style={{ width: 215 }}>
              <WarriorLoginForm
                item={{ name: 'login', label: t('login'), type: 'TEXT' }}
                fields={fields}
                setFields={setFields}
                selectedWarrior={selectedWarrior}
                setSelectedWarrior={setSelectedWarrior}
              />
            </View>
            <Button onPress={onAdd}>{t('add')}</Button>
            
          </View>
          

          {/* Friends grid */}
          <View style={{ marginTop: MARGIN_10 * 2 }}>
            <View
              style={{
                flexDirection: 'row',
                borderBottomWidth: 1,
                borderBottomColor: GOLDEN100,
                paddingBottom: 4,
              }}
            >
              <Text bold style={{ flex: 2 }}>
                {t('friends.columns.warrior')}
              </Text>
              <Text bold style={{ flex: 1 }}>
                {t('friends.columns.mentor')}
              </Text>
            </View>
            {friendsFetching ? (
              <ActivityIndicator color={BASE_COLOR} style={{ marginTop: MARGIN_10 }} />
            ) : friends.length === 0 ? (
              <Text style={{ marginTop: MARGIN_5 }}>{t('friends.noFriends')}</Text>
            ) : (
              friends.map((f) => {
                const w = friendWarriors[f.warrior];
                return (
                  <View
                    key={f.warrior}
                    style={{ flexDirection: 'row', marginTop: 4, alignItems: 'center' }}
                  >
                    <View style={{ flex: 2, flexDirection: 'row', alignItems: 'center' }}>
                      <Text style={{ marginRight: MARGIN_5 }}>
                        {f.warrior}
                        {w ? ` [${w.level}]` : ''}
                      </Text>
                      {w ? (
                        <InfoIcon
                          style={{}}
                          iconStyle={{ width: pOrT(16, 18), height: pOrT(16, 18) }}
                          onPress={() => warriorInfoModalStore.toggle(w.id, true)}
                        />
                      ) : null}
                    </View>
                    <Text style={{ flex: 1 }}>{f.mentorTo ? '★' : ''}</Text>
                  </View>
                );
              })
            )}
          </View>

          {/* Mentor bonuses table (collapsible) */}
          <View style={{ marginTop: MARGIN_10 * 2 }}>
            <TouchableOpacity
              onPress={() => setBonusesExpanded(!bonusesExpanded)}
              style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 4 }}
            >
              <Image
                source={require('../assets/images/arrowDown.png')}
                style={{
                  width: pOrT(10, 12),
                  height: pOrT(10, 12),
                  marginRight: MARGIN_5,
                  transform: [{ rotate: bonusesExpanded ? '0deg' : '-90deg' }],
                }}
              />
              <Text bold>{t('friends.bonusesTable.title')}</Text>
            </TouchableOpacity>
            {bonusesExpanded ? (
              <View style={{ marginTop: MARGIN_5 }}>
                <View
                  style={{
                    flexDirection: 'row',
                    borderBottomWidth: 1,
                    borderBottomColor: GOLDEN100,
                    paddingBottom: 4,
                  }}
                >
                  <Text bold style={{ flex: 1 }}>
                    {t('friends.bonusesTable.level')}
                  </Text>
                  <Text bold style={{ flex: 2 }}>
                    {t('friends.bonusesTable.reward')}
                  </Text>
                </View>
                {mentorBonuses.map((b) => (
                  <View key={b.level} style={{ flexDirection: 'row', marginTop: 4 }}>
                    <Text style={{ flex: 1 }}>{b.level}</Text>
                    <Text style={{ flex: 2 }}>
                      {b.bonus.money ? `${b.bonus.money} gold` : ''}
                    </Text>
                  </View>
                ))}
              </View>
            ) : null}
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
}
